import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  CreditCard, 
  BookOpen, 
  CheckCircle2, 
  Loader2,
  Plus
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Subject display names
const SUBJECT_NAMES = {
  math: "Mathematics",
  science: "Science",
  english: "English Grammar",
  hindi: "Hindi Vyakaran",
  physics: "Physics",
  chemistry: "Chemistry",
  biology: "Biology"
};
// Physics/Chemistry/Biology are offered as separate subjects only for Grade 7-8.
const BASE_SUBJECTS = ["math", "science", "english", "hindi"];
const EXTENDED_SUBJECTS_78 = ["physics", "chemistry", "biology"];
const subjectsForGrade = (grade) => {
  const g = parseInt(grade, 10);
  return (g >= 7) ? [...BASE_SUBJECTS, ...EXTENDED_SUBJECTS_78] : BASE_SUBJECTS;
};

// Plan colors and details
const PLAN_DETAILS = {
  starter: { 
    name: "Starter", 
    color: "emerald", 
    bgGradient: "from-emerald-500 to-emerald-600",
    description: "1 hour/day learning"
  },
  plus: { 
    name: "Plus", 
    color: "blue", 
    bgGradient: "from-blue-500 to-blue-600",
    description: "2 hours/day + certificates"
  },
  pro: { 
    name: "Pro", 
    color: "purple", 
    bgGradient: "from-purple-500 to-purple-600",
    description: "Unlimited + study plans"
  }
};

export function ChildSubscriptionManager({ childrenList, selectedChild, onChildSelect }) {
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [childrenSummary, setChildrenSummary] = useState([]);
  const [multiChildDiscount, setMultiChildDiscount] = useState(null);
  
  // Subscribe modal state
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [subscribeChild, setSubscribeChild] = useState(null);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("starter");
  const [selectedDuration, setSelectedDuration] = useState("monthly");
  const [pricing, setPricing] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    fetchPlansAndSubscriptions();
  }, []);

  const fetchPlansAndSubscriptions = async () => {
    setLoading(true);
    try {
      // Fetch plans
      const plansRes = await fetch(`${API}/child-subscriptions/plans`, {
        credentials: "include"
      });
      const plansData = await plansRes.json();
      
      if (plansData.success) {
        setPlans(plansData.plans);
        setMultiChildDiscount(plansData.multi_child_discount);
      }
      
      // Fetch children summary
      const childrenRes = await fetch(`${API}/child-subscriptions/children`, {
        credentials: "include"
      });
      const childrenData = await childrenRes.json();
      
      if (childrenData.success) {
        setChildrenSummary(childrenData.children);
      }
    } catch (error) {
      console.error("Failed to fetch subscription data:", error);
      toast.error("Failed to load subscription data");
    } finally {
      setLoading(false);
    }
  };

  const openSubscribeModal = (child) => {
    setSubscribeChild(child);
    setSelectedSubjects(["math", "science"]); // Default subjects
    setSelectedPlan("starter");
    setSelectedDuration("monthly");
    setPricing(null);
    setShowSubscribeModal(true);
  };

  const calculatePricing = async () => {
    if (!subscribeChild || selectedSubjects.length === 0) return;
    
    setLoadingPricing(true);
    try {
      const res = await fetch(`${API}/child-subscriptions/calculate-price`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          student_id: subscribeChild.student_id,
          subjects: selectedSubjects,
          plan_type: selectedPlan,
          duration: selectedDuration
        })
      });
      
      const data = await res.json();
      if (data.success) {
        setPricing(data.pricing);
      } else {
        toast.error(data.detail || "Failed to calculate pricing");
      }
    } catch (error) {
      toast.error("Failed to calculate pricing");
    } finally {
      setLoadingPricing(false);
    }
  };

  useEffect(() => {
    if (showSubscribeModal && subscribeChild && selectedSubjects.length > 0) {
      calculatePricing();
    }
  }, [subscribeChild, selectedSubjects, selectedPlan, selectedDuration, showSubscribeModal]);

  const handleSubscribe = async () => {
    if (!subscribeChild || selectedSubjects.length === 0) {
      toast.error("Please select at least one subject");
      return;
    }
    
    setSubscribing(true);
    try {
      const res = await fetch(`${API}/child-subscriptions/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          student_id: subscribeChild.student_id,
          subjects: selectedSubjects,
          plan_type: selectedPlan,
          duration: selectedDuration
        })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success("Subscription created successfully!");
        setShowSubscribeModal(false);
        fetchPlansAndSubscriptions();
      } else {
        toast.error(data.detail || "Failed to create subscription");
      }
    } catch (error) {
      toast.error("Failed to create subscription");
    } finally {
      setSubscribing(false);
    }
  };

  const toggleSubject = (subject) => {
    setSelectedSubjects(prev => 
      prev.includes(subject) 
        ? prev.filter(s => s !== subject)
        : [...prev, subject]
    );
  };

  const getChildSummary = (studentId) => {
    return childrenSummary.find(c => c.student_id === studentId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading subscriptions...</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Per-Child EduWise Subscription Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {childrenList.map((child, idx) => {
          const summary = getChildSummary(child.student_id);
          const hasActiveSubscription = summary?.active_subjects?.length > 0;
          
          return (
            <Card 
              key={child.student_id} 
              className={`relative overflow-hidden transition-all hover:shadow-md ${
                selectedChild?.student_id === child.student_id 
                  ? 'ring-2 ring-blue-500 bg-blue-50/30' 
                  : 'bg-white'
              }`}
              data-testid={`child-card-${child.student_id}`}
            >
              <div className="p-3 space-y-3">
                {/* Row 1: Child Info + Subscribe Button */}
                <div className="flex items-center gap-2.5">
                  {/* Avatar */}
                  <div className="w-9 h-9 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {child.name?.charAt(0) || 'C'}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <h3 className="font-semibold text-gray-900 text-sm truncate">{child.name}</h3>
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${hasActiveSubscription ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                    </div>
                    <p className="text-xs text-gray-500">Grade {child.grade}</p>
                  </div>
                  
                  {/* Subscribe Button */}
                  <Button 
                    size="sm"
                    variant={hasActiveSubscription ? "outline" : "default"}
                    className={hasActiveSubscription ? "text-xs h-7 px-2" : "text-xs h-7 px-2 bg-blue-600 hover:bg-blue-700"}
                    onClick={() => openSubscribeModal(child)}
                    data-testid={`subscribe-${child.student_id}-btn`}
                  >
                    <Plus className="w-3 h-3 mr-0.5" />
                    {hasActiveSubscription ? 'Add' : 'Subscribe'}
                  </Button>
                </div>

                {/* Row 2: EduWise Subjects & Price - Wrapped properly */}
                <div className="pl-11 space-y-1.5">
                  <div className="flex items-center gap-1">
                    <BookOpen className="w-3 h-3 text-blue-600 flex-shrink-0" />
                    <span className="text-[10px] text-gray-500 font-medium">EduWise:</span>
                    {summary?.total_monthly_cost > 0 && (
                      <span className="text-xs font-semibold text-gray-700 ml-auto">₹{summary.total_monthly_cost?.toFixed(0) || 0}/mo</span>
                    )}
                  </div>
                  {hasActiveSubscription ? (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {summary.active_subjects.map(subject => (
                        <span 
                          key={subject} 
                          className="px-2 py-1 rounded text-[11px] font-medium bg-blue-100 text-blue-700"
                        >
                          {SUBJECT_NAMES[subject] || subject}
                        </span>
                      ))}
                      {summary.discount_percent > 0 && (
                        <Badge className="bg-emerald-100 text-emerald-700 text-[10px] py-0.5 px-1.5">
                          {summary.discount_percent}% off
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <span className="text-[11px] text-gray-400 italic">Not subscribed</span>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Subscribe Modal - Compact */}
      <Dialog open={showSubscribeModal} onOpenChange={setShowSubscribeModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-2">
            <DialogTitle className="flex items-center gap-2 text-base">
              <BookOpen className="w-4 h-4 text-blue-600" />
              Subscribe for {subscribeChild?.name}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Select subjects and plan
              {subscribeChild && getChildSummary(subscribeChild.student_id)?.discount_percent > 0 && (
                <Badge className="ml-2 bg-emerald-500 text-[10px] py-0">
                  {getChildSummary(subscribeChild.student_id)?.discount_percent}% Discount
                </Badge>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Subject Selection - Compact */}
            <div>
              <Label className="text-xs font-medium">Select Subjects</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {subjectsForGrade(subscribeChild?.grade).map((key) => (
                  <div 
                    key={key}
                    data-testid={`subscribe-subject-${key}`}
                    className={`flex items-center gap-2 p-2 rounded-md border cursor-pointer transition-colors ${
                      selectedSubjects.includes(key) 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => toggleSubject(key)}
                  >
                    <Checkbox 
                      checked={selectedSubjects.includes(key)}
                      onCheckedChange={() => toggleSubject(key)}
                      className="h-4 w-4"
                    />
                    <span className="text-xs font-medium">{SUBJECT_NAMES[key] || key}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Plan Selection - Compact */}
            <div>
              <Label className="text-xs font-medium">Select Plan</Label>
              <RadioGroup value={selectedPlan} onValueChange={setSelectedPlan} className="mt-1.5">
                <div className="grid grid-cols-3 gap-2">
                  {plans.map(plan => (
                    <div key={plan.id} className="relative">
                      <RadioGroupItem value={plan.id} id={plan.id} className="peer sr-only" />
                      <Label
                        htmlFor={plan.id}
                        className={`flex flex-col items-center p-2 rounded-md border cursor-pointer transition-colors peer-data-[state=checked]:border-blue-500 peer-data-[state=checked]:bg-blue-50 hover:border-gray-300`}
                      >
                        <span className="font-semibold text-sm">{plan.name}</span>
                        <span className="text-xs text-gray-500">₹{plan.base_price_per_subject}/sub</span>
                      </Label>
                    </div>
                  ))}
                </div>
              </RadioGroup>
            </div>
            
            {/* Duration Selection - Compact */}
            <div>
              <Label className="text-xs font-medium">Duration</Label>
              <RadioGroup value={selectedDuration} onValueChange={setSelectedDuration} className="mt-1.5">
                <div className="grid grid-cols-3 gap-2">
                  <div className="relative">
                    <RadioGroupItem value="monthly" id="monthly" className="peer sr-only" />
                    <Label htmlFor="monthly" className="flex items-center justify-center p-2 rounded-md border cursor-pointer transition-colors peer-data-[state=checked]:border-blue-500 peer-data-[state=checked]:bg-blue-50 hover:border-gray-300">
                      <span className="text-xs font-medium">Monthly</span>
                    </Label>
                  </div>
                  <div className="relative">
                    <RadioGroupItem value="3_months" id="3_months" className="peer sr-only" />
                    <Label htmlFor="3_months" className="flex items-center justify-center p-2 rounded-md border cursor-pointer transition-colors peer-data-[state=checked]:border-blue-500 peer-data-[state=checked]:bg-blue-50 hover:border-gray-300">
                      <span className="text-xs font-medium">3 Months</span>
                    </Label>
                  </div>
                  <div className="relative">
                    <RadioGroupItem value="6_months" id="6_months" className="peer sr-only" />
                    <Label htmlFor="6_months" className="flex items-center justify-center p-2 rounded-md border cursor-pointer transition-colors peer-data-[state=checked]:border-blue-500 peer-data-[state=checked]:bg-blue-50 hover:border-gray-300">
                      <span className="text-xs font-medium">6 Months</span>
                    </Label>
                  </div>
                </div>
              </RadioGroup>
            </div>
            
            {/* Pricing Summary - Compact */}
            {loadingPricing ? (
              <div className="flex items-center justify-center py-2">
                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                <span className="ml-2 text-gray-500 text-xs">Calculating...</span>
              </div>
            ) : pricing && (
              <div className="bg-gray-50 rounded-md p-3 space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Subtotal ({selectedSubjects.length} sub × {selectedDuration === 'monthly' ? '1 mo' : selectedDuration === '3_months' ? '3 mo' : '6 mo'})</span>
                  <span>₹{pricing.subtotal.toFixed(0)}</span>
                </div>
                {pricing.multi_child_discount_amount > 0 && (
                  <div className="flex justify-between text-xs text-emerald-600">
                    <span>Multi-Child Discount ({pricing.multi_child_discount_percent}%)</span>
                    <span>-₹{pricing.multi_child_discount_amount.toFixed(0)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold pt-1.5 border-t">
                  <span className="text-sm">Total</span>
                  <span className="text-blue-600">₹{pricing.final_price.toFixed(0)}</span>
                </div>
                <p className="text-[10px] text-gray-400 text-center">
                  ₹{pricing.price_per_month.toFixed(0)}/month effective
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter className="pt-2">
            <Button variant="outline" size="sm" onClick={() => setShowSubscribeModal(false)}>
              Cancel
            </Button>
            <Button 
              size="sm"
              onClick={handleSubscribe}
              disabled={subscribing || selectedSubjects.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {subscribing ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-3 h-3 mr-1.5" />
                  Subscribe
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ChildSubscriptionManager;
